import { useEffect, useRef, useState } from 'react';
import { createJob, uploadToS3 } from '../api';

interface Props {
  token: string;
  onUploaded: () => void | Promise<void>;
}

interface UploadState {
  fileName: string;
  percent: number;
}

const ACCEPT = '.mp3,.m4a,.wav,.flac,.ogg,.webm,.mp4,audio/*';

/**
 * The same people and the same project vocabulary come back every jour fixe, so
 * the two hint lists are remembered in the browser. Per device, never sent
 * anywhere except with the next upload.
 */
const STORAGE_KEY = 'meeting-transcriber.hints';

interface Hints {
  participants: string;
  terms: string;
}

const EMPTY: Hints = { participants: '', terms: '' };

function loadHints(): Hints {
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    return stored ? { ...EMPTY, ...(JSON.parse(stored) as Partial<Hints>) } : EMPTY;
  } catch {
    // Private windows and blocked site data both land here; the panel works without it.
    return EMPTY;
  }
}

/** One entry per line, blanks dropped. */
function toList(value: string): string[] {
  return value
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean);
}

export function UploadPanel({ token, onUploaded }: Props) {
  const [upload, setUpload] = useState<UploadState>();
  const [error, setError] = useState<string>();
  const [dragging, setDragging] = useState(false);
  const [hints, setHints] = useState<Hints>(loadHints);
  const [showHints, setShowHints] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(hints));
    } catch {
      // Not being able to remember the list is not worth an error message.
    }
  }, [hints]);

  async function handleFiles(files: FileList | null) {
    const file = files?.[0];
    if (!file) return;

    setError(undefined);
    setUpload({ fileName: file.name, percent: 0 });
    try {
      const { uploadUrl, contentType } = await createJob(token, file, {
        participants: toList(hints.participants),
        terms: toList(hints.terms),
      });
      await uploadToS3(uploadUrl, file, contentType, (percent) =>
        setUpload({ fileName: file.name, percent }),
      );
      // The S3 event kicks off the pipeline; the list picks it up on the next poll.
      await onUploaded();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Upload fehlgeschlagen');
    } finally {
      setUpload(undefined);
      if (inputRef.current) inputRef.current.value = '';
    }
  }

  const hintCount = toList(hints.participants).length + toList(hints.terms).length;

  return (
    <section
      className={`upload ${dragging ? 'dragging' : ''}`}
      onDragOver={(event) => {
        event.preventDefault();
        setDragging(true);
      }}
      onDragLeave={() => setDragging(false)}
      onDrop={(event) => {
        event.preventDefault();
        setDragging(false);
        void handleFiles(event.dataTransfer.files);
      }}
    >
      <input
        ref={inputRef}
        type="file"
        accept={ACCEPT}
        hidden
        onChange={(event) => void handleFiles(event.target.files)}
      />

      {upload ? (
        <>
          <p>
            <strong>{upload.fileName}</strong> wird hochgeladen … {upload.percent}%
          </p>
          <div className="progress">
            <div className="progress-bar" style={{ width: `${upload.percent}%` }} />
          </div>
        </>
      ) : (
        <>
          <p className="upload-hint">Audiodatei hierher ziehen</p>
          <button className="primary" onClick={() => inputRef.current?.click()}>
            Datei auswählen
          </button>
          <p className="muted small">MP3, M4A, WAV, FLAC, OGG · bis 2 GB / 4 Stunden</p>

          <button className="link" onClick={() => setShowHints((open) => !open)}>
            {showHints ? 'Angaben ausblenden' : 'Teilnehmende und Fachbegriffe'}
            {!showHints && hintCount > 0 ? ` (${hintCount})` : ''}
          </button>

          {showHints && (
            <div className="hints">
              <label>
                Teilnehmende
                <textarea
                  rows={4}
                  value={hints.participants}
                  placeholder={'Julia Hördler\nDieter Braun\nJulia Lossnitzer'}
                  onChange={(event) =>
                    setHints((current) => ({ ...current, participants: event.target.value }))
                  }
                />
              </label>
              <label>
                Fachbegriffe und Eigennamen
                <textarea
                  rows={4}
                  value={hints.terms}
                  placeholder={'AWO Chemnitz\nThreema\nKita Naseweis\nSAB'}
                  onChange={(event) =>
                    setHints((current) => ({ ...current, terms: event.target.value }))
                  }
                />
              </label>
              <p className="muted small">
                Ein Eintrag pro Zeile. Die Namen helfen beim Zuordnen der Sprecher, die Begriffe
                gegen Verhörer der Spracherkennung. Bleibt für das nächste Mal gespeichert.
              </p>
            </div>
          )}
        </>
      )}

      {error && <p className="error">{error}</p>}
    </section>
  );
}
